import { useMemo, useState } from 'react';
import { Alert, Button, Grid, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import type { Principal, Tenant } from '../../app/types';
import PageSection from '../shared/PageSection';

interface TenantsPageProps {
  readonly principal: Principal;
  readonly tenants: Tenant[];
  readonly selectedTenantId: string;
  readonly onSelectTenant: (tenantId: string) => void;
  readonly onCreateTenant: (payload: { displayName: string; collectionMode: 'shared' | 'dedicated' }) => Promise<void>;
}

export default function TenantsPage({
  principal,
  tenants,
  selectedTenantId,
  onSelectTenant,
  onCreateTenant,
}: TenantsPageProps) {
  const [displayName, setDisplayName] = useState('');
  const [collectionMode, setCollectionMode] = useState<'shared' | 'dedicated'>('shared');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const selectedTenant = useMemo(
    () => tenants.find((tenant) => tenant.tenantId === selectedTenantId) ?? null,
    [selectedTenantId, tenants]
  );

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      await onCreateTenant({ displayName, collectionMode });
      setDisplayName('');
      setCollectionMode('shared');
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Unable to create tenant.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} lg={7}>
        <PageSection title="Tenant Directory" subtitle="All tenant visibility available to the current principal.">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tenant</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Collection Mode</TableCell>
                <TableCell>Retention</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tenants.map((tenant) => (
                <TableRow
                  key={tenant.tenantId}
                  hover
                  selected={tenant.tenantId === selectedTenantId}
                  onClick={() => onSelectTenant(tenant.tenantId)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <strong>{tenant.displayName}</strong>
                    <br />
                    <Typography variant="caption" color="text.secondary">
                      {tenant.tenantId}
                    </Typography>
                  </TableCell>
                  <TableCell>{tenant.status}</TableCell>
                  <TableCell>{tenant.settings.collectionMode}</TableCell>
                  <TableCell>{tenant.settings.retentionDays} days</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </PageSection>
      </Grid>

      <Grid item xs={12} lg={5}>
        <Stack spacing={3}>
          <PageSection title="Active Tenant Scope">
            {selectedTenant ? (
              <Stack spacing={1}>
                <Typography><strong>Name:</strong> {selectedTenant.displayName}</Typography>
                <Typography><strong>ID:</strong> {selectedTenant.tenantId}</Typography>
                <Typography><strong>Collection:</strong> {selectedTenant.settings.collectionMode}</Typography>
                <Typography><strong>Threshold:</strong> {selectedTenant.settings.matchThreshold}%</Typography>
              </Stack>
            ) : (
              <Typography color="text.secondary">No tenant selected.</Typography>
            )}
          </PageSection>

          {principal.role === 'platform-admin' ? (
            <PageSection title="Create Tenant" subtitle="Platform-admin only.">
              <Stack spacing={2}>
                {error ? <Alert severity="error">{error}</Alert> : null}
                <TextField label="Display Name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
                <TextField
                  select
                  label="Collection Mode"
                  value={collectionMode}
                  onChange={(event) => setCollectionMode(event.target.value as 'shared' | 'dedicated')}
                >
                  <MenuItem value="shared">Shared</MenuItem>
                  <MenuItem value="dedicated">Dedicated</MenuItem>
                </TextField>
                <Button variant="contained" onClick={() => void submit()} disabled={!displayName || saving}>
                  {saving ? 'Creating...' : 'Create Tenant'}
                </Button>
              </Stack>
            </PageSection>
          ) : null}
        </Stack>
      </Grid>
    </Grid>
  );
}
